import { useId, useState } from "react";

// Gráficas del portal, dibujadas a mano en SVG.
//
// # Por qué sin librería
//
// La CSP del portal es `script-src 'self'`: nada de CDN, así que cualquier
// librería entra al bundle. Recharts y compañía pesan más que todo el portal
// junto para dibujar cuatro rectángulos, y lo que necesitamos aquí son barras
// y una escala lineal. El día que haga falta un eje de tiempo real o zoom, esa
// librería se justifica; hoy no.
//
// # Reglas que siguen todas
//
//   - **Nunca dos escalas en un eje.** Dos magnitudes distintas son dos
//     gráficas, no una con doble eje: el doble eje deja al lector comparando
//     alturas que no son comparables.
//   - **Ningún dato inventado.** Sin datos no se dibujan ejes vacíos: se dice
//     que no hay datos. Un eje con cero barras se lee como «no pasó nada»
//     cuando muchas veces significa «no se pudo leer» (CLAUDE.md §6).
//   - **El color nunca es el único canal.** Los resultados llevan su etiqueta
//     al lado; el color acompaña, no informa por sí solo.
//   - **Todo valor que enseña el tooltip es alcanzable sin ratón**, en la
//     tabla que cada gráfica despliega debajo.

// Paleta. El portal es oscuro, así que son los pasos elegidos para fondo
// oscuro, no los claros con el brillo bajado.
export const VIZ = {
  serie: "#3987e5",
  serieTenue: "rgba(57, 135, 229, 0.18)",
  rejilla: "rgba(148, 163, 184, 0.14)",
  // Estados: paleta fija de estado, nunca reutilizada como color de serie.
  bueno: "#0ca30c",
  aviso: "#fab219",
  serio: "#ec835a",
  critico: "#d03b3b",
  neutro: "#64748b",
} as const;

/** Un punto de una serie temporal o categórica. */
export type Punto = {
  /** Lo que va en el eje: una fecha corta, una hora, un destino. */
  etiqueta: string;
  valor: number;
  /** Texto completo para el tooltip; si falta, se usa la etiqueta. */
  detalle?: string;
};

/** Formatea el valor para el eje y el tooltip. */
export type Formato = (valor: number) => string;

const formatoPorOmision: Formato = (v) => v.toLocaleString("es-MX");

/**
 * Columnas: una sola serie a lo largo del tiempo.
 *
 * Una serie y no varias a propósito: «llamadas por día» y «minutos por día»
 * son dos preguntas distintas y se responden en dos gráficas, que es lo que
 * deja comparar días sin que una magnitud aplaste a la otra.
 */
export function Columnas({
  datos,
  formato = formatoPorOmision,
  vacio,
  alto = 160,
  titulo,
}: {
  datos: Punto[];
  formato?: Formato;
  /** Qué decir cuando no hay nada que dibujar. */
  vacio: string;
  alto?: number;
  /** Descripción para quien no ve la gráfica. */
  titulo: string;
}) {
  const [activo, setActivo] = useState<number | null>(null);
  const id = useId();

  if (datos.length === 0) return <SinDatos texto={vacio} />;

  const maximo = Math.max(...datos.map((d) => d.valor));
  // Un máximo de cero no es una gráfica: son barras de altura cero, que se
  // leen como un eje roto. Se dice que no hubo nada y se acabó.
  if (maximo <= 0) return <SinDatos texto={vacio} />;

  // El tope redondeado deja aire encima de la barra más alta; pegada al borde
  // parece recortada.
  const tope = escalaBonita(maximo);
  const indiceMaximo = datos.findIndex((d) => d.valor === maximo);

  // Las columnas se dibujan con cajas y no en SVG a propósito: un viewBox
  // estirado al ancho del contenedor deforma el radio de la esquina y la
  // separación entre barras, que es justo lo que hace que un grupo de
  // columnas se lea como columnas. En CSS, 2px son 2px.
  return (
    <figure className="relative m-0" role="img" aria-labelledby={id}>
      <figcaption id={id} className="sr-only">
        {titulo}
      </figcaption>

      <div className="relative" style={{ height: alto }}>
        {[0, 0.5, 1].map((fraccion) => (
          <span
            key={fraccion}
            aria-hidden
            className="absolute inset-x-0 border-t"
            style={{ bottom: `${fraccion * 100}%`, borderColor: VIZ.rejilla }}
          />
        ))}

        <div className="absolute inset-0 flex items-end gap-[2px]">
          {datos.map((punto, i) => (
            <button
              key={punto.etiqueta}
              type="button"
              // El objetivo del puntero es la banda entera y no la barra:
              // nadie acierta a una columna de seis píxeles.
              className="group flex h-full flex-1 cursor-default items-end justify-center"
              onPointerEnter={() => setActivo(i)}
              onPointerLeave={() => setActivo(null)}
              onFocus={() => setActivo(i)}
              onBlur={() => setActivo(null)}
              aria-label={`${punto.detalle ?? punto.etiqueta}: ${formato(punto.valor)}`}
            >
              <span
                className="w-full max-w-[24px] rounded-t-[4px] transition-opacity"
                style={{
                  // Mínimo de 2px para que un día con una sola llamada se vea;
                  // cero no pinta nada, que es la verdad.
                  height:
                    punto.valor > 0 ? `max(2px, ${(punto.valor / tope) * 100}%)` : "0",
                  backgroundColor: VIZ.serie,
                  opacity: activo === null || activo === i ? 1 : 0.5,
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Etiquetas del eje: solo los extremos y el máximo. Una etiqueta por
          columna se convierte en una mancha ilegible. */}
      <div className="mt-1.5 flex justify-between text-[0.65rem] text-slate-500">
        <span>{datos[0]?.etiqueta}</span>
        <span className="text-slate-400">
          máx. {formato(maximo)}
          {indiceMaximo >= 0 && ` · ${datos[indiceMaximo]?.etiqueta}`}
        </span>
        <span>{datos[datos.length - 1]?.etiqueta}</span>
      </div>

      {activo !== null && datos[activo] && (
        <Tooltip
          posicion={(activo + 0.5) / datos.length}
          valor={formato(datos[activo].valor)}
          etiqueta={datos[activo].detalle ?? datos[activo].etiqueta}
        />
      )}

      <Tabla datos={datos} formato={formato} />
    </figure>
  );
}

/**
 * Barras horizontales ordenadas: comparar magnitudes con nombres largos.
 *
 * Horizontal y no vertical porque los nombres —un prefijo, un destino— no
 * caben debajo de una columna sin girarse, y el texto girado no se lee.
 */
export function BarrasOrdenadas({
  datos,
  formato = formatoPorOmision,
  vacio,
  titulo,
}: {
  datos: Punto[];
  formato?: Formato;
  vacio: string;
  titulo: string;
}) {
  if (datos.length === 0) return <SinDatos texto={vacio} />;
  const maximo = Math.max(...datos.map((d) => d.valor));
  if (maximo <= 0) return <SinDatos texto={vacio} />;

  return (
    <div className="space-y-2" role="img" aria-label={titulo}>
      {datos.map((punto) => (
        <div key={punto.etiqueta} className="grid grid-cols-[6rem_1fr_auto] items-center gap-3">
          <span className="truncate font-mono text-xs text-slate-400" title={punto.detalle ?? punto.etiqueta}>
            {punto.etiqueta}
          </span>
          <span className="h-2.5 overflow-hidden rounded-sm bg-white/[0.04]">
            <span
              className="block h-full rounded-sm"
              style={{
                width: `${Math.max((punto.valor / maximo) * 100, 1)}%`,
                backgroundColor: VIZ.serie,
              }}
            />
          </span>
          <span className="tabular-nums text-xs text-slate-300">{formato(punto.valor)}</span>
        </div>
      ))}
    </div>
  );
}

/** Un trozo de la barra de resultados. */
export type Tramo = {
  etiqueta: string;
  valor: number;
  color: string;
};

/**
 * Barra apilada de resultados: en qué acabaron las llamadas.
 *
 * Apilada y no un donut porque lo que importa es la proporción entre cuatro o
 * cinco clases, y en un donut eso se compara peor. Cada tramo lleva su nombre
 * y su número debajo: el color ordena, pero no es lo que informa.
 */
export function BarraDeResultados({ tramos, vacio }: { tramos: Tramo[]; vacio: string }) {
  const total = tramos.reduce((suma, t) => suma + t.valor, 0);
  if (total === 0) return <SinDatos texto={vacio} />;

  const visibles = tramos.filter((t) => t.valor > 0);

  return (
    <div>
      {/* El hueco de 2px entre tramos lo hace el `gap`: separa sin dibujar un
          borde, que sería tinta que no es dato. */}
      <div className="flex h-3 gap-[2px] overflow-hidden rounded-sm">
        {visibles.map((tramo) => (
          <span
            key={tramo.etiqueta}
            className="h-full first:rounded-l-sm last:rounded-r-sm"
            style={{
              width: `${(tramo.valor / total) * 100}%`,
              backgroundColor: tramo.color,
            }}
            title={`${tramo.etiqueta}: ${tramo.valor}`}
          />
        ))}
      </div>

      <ul className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {visibles.map((tramo) => (
          <li key={tramo.etiqueta} className="flex items-baseline gap-2 text-xs">
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 translate-y-[-1px] rounded-[2px]"
              style={{ backgroundColor: tramo.color }}
            />
            <span className="text-slate-400">{tramo.etiqueta}</span>
            <span className="ml-auto tabular-nums text-slate-200">
              {tramo.valor}
              <span className="ml-1 text-slate-500">
                {((tramo.valor / total) * 100).toFixed(0)} %
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tooltip({
  posicion,
  valor,
  etiqueta,
}: {
  /** 0 a 1 a lo ancho de la gráfica. */
  posicion: number;
  valor: string;
  etiqueta: string;
}) {
  return (
    // Dentro del área de la gráfica y no encima de ella: sacarlo por arriba
    // tapaba el título de la tarjeta, que es justo lo que dice qué se está
    // mirando. Arriba del todo hay sitio porque ahí solo llega la barra más
    // alta, y para esa el valor ya está en la etiqueta del eje.
    <div
      className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-[#0b0f1d]/95 px-2.5 py-1.5 shadow-lg"
      style={{ left: `${Math.min(Math.max(posicion * 100, 10), 90)}%` }}
    >
      {/* El número manda y el nombre acompaña: quien pasa el ratón ya sabe
          sobre qué está, lo que busca es el valor. */}
      <div className="text-sm font-semibold tabular-nums text-slate-100">{valor}</div>
      <div className="text-[0.65rem] text-slate-400">{etiqueta}</div>
    </div>
  );
}

function SinDatos({ texto }: { texto: string }) {
  return (
    <p className="flex h-[120px] items-center justify-center rounded-lg border border-dashed border-white/10 px-4 text-center text-xs text-slate-500">
      {texto}
    </p>
  );
}

/**
 * La tabla con los mismos números.
 *
 * Plegada para no competir con la gráfica, pero presente: es lo que hace que
 * los valores no dependan de pasar el ratón, que con teclado o lector de
 * pantalla no siempre ocurre.
 */
function Tabla({ datos, formato }: { datos: Punto[]; formato: Formato }) {
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-[0.65rem] text-slate-500 transition hover:text-slate-300">
        Ver los datos
      </summary>
      <table className="mt-2 w-full text-left text-xs">
        <tbody>
          {datos.map((punto) => (
            <tr key={punto.etiqueta} className="border-t border-white/5">
              <td className="py-1 pr-3 text-slate-400">{punto.detalle ?? punto.etiqueta}</td>
              <td className="py-1 text-right tabular-nums text-slate-200">
                {formato(punto.valor)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/**
 * escalaBonita redondea el tope del eje hacia arriba a un número limpio.
 *
 * Sin esto la barra más alta toca el borde y parece recortada, y el eje acaba
 * en 37 en lugar de en 40.
 */
function escalaBonita(maximo: number): number {
  if (maximo <= 5) return 5;
  const magnitud = Math.pow(10, Math.floor(Math.log10(maximo)));
  const normalizado = maximo / magnitud;
  const paso = normalizado <= 2 ? 2 : normalizado <= 5 ? 5 : 10;
  return paso * magnitud;
}
